/**
 * Geofencing Utilities
 *
 * Detect state codes from domain names for automatic regional filtering.
 * Supports both TLD and subdomain patterns as defined in Issue #1326.
 *
 * Examples:
 * - ri.localartistfairs.com → "RI"
 * - localartistfairs.ri → "RI"
 * - localartistfairs.com → null
 */

// All 50 US state codes (uppercase)
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
]);

/**
 * Extract state code from hostname
 *
 * Supports two patterns:
 * 1. Subdomain: ri.localartistfairs.com → "RI"
 * 2. TLD: localartistfairs.ri → "RI"
 *
 * @param hostname - The hostname to parse (e.g., window.location.hostname)
 * @returns Uppercase state code (e.g., "RI") or null if no state detected
 */
export function getStateFromHostname(hostname: string = window.location.hostname): string | null {
  if (!hostname) {
    return null;
  }

  // Remove port if present (e.g., "localhost:8080" → "localhost")
  const cleanHostname = hostname.split(':')[0];

  // Split hostname into parts
  const parts = cleanHostname.split('.');

  // Pattern 1: Subdomain (ri.localartistfairs.com)
  // Check if first part is a valid state code
  if (parts.length >= 3) {
    const potentialState = parts[0].toUpperCase();
    if (US_STATE_CODES.has(potentialState)) {
      return potentialState;
    }
  }

  // Pattern 2: TLD (localartistfairs.ri)
  // Check if last part is a valid state code
  if (parts.length >= 2) {
    const potentialState = parts[parts.length - 1].toUpperCase();
    if (US_STATE_CODES.has(potentialState)) {
      return potentialState;
    }
  }

  // No state code found
  return null;
}

/**
 * Check if geofencing is active for the current hostname
 *
 * @returns true if a state code was detected from the hostname
 */
export function isGeofencingActive(): boolean {
  return getStateFromHostname() !== null;
}

/**
 * Get the full state name from a state code
 *
 * @param stateCode - Two-letter state code (e.g., "RI")
 * @returns Full state name (e.g., "Rhode Island") or the code if not found
 */
export function getStateName(stateCode: string): string {
  const stateNames: Record<string, string> = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
    'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
    'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
    'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
    'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
    'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
    'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
    'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
    'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
    'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
    'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  return stateNames[stateCode.toUpperCase()] || stateCode;
}
